// Single source of truth for the onsite permission taxonomy (P4 — moved from the
// BE-local apps/traba-server-node/src/auth/onsite-permissions.taxonomy.ts so the FE
// consumes the same vocabulary the BE enforces, with no hand-sync). The grants
// column is a plain String (P0), so DB↔enum drift is caught by a BE tripwire test
// (every stored value ∈ this enum) + grant-DTO rejection of non-members.
export enum OnsitePermission {
  WORKER_READ = 'worker:read',
  WORKER_MANAGE = 'worker:manage',
  WORKER_DOCUMENTS = 'worker:documents',
  TIMESHEET_READ = 'timesheet:read',
  TIMESHEET_MANAGE = 'timesheet:manage',
  PAY_READ = 'pay:read',
  PAY_MANAGE = 'pay:manage',
  SHIFT_READ = 'shift:read',
  SHIFT_MANAGE = 'shift:manage',
  SCHEDULE_MANAGE = 'schedule:manage',
  COMPANY_READ = 'company:read',
  COMPANY_MANAGE = 'company:manage',
  CONNECTION_MANAGE = 'connection:manage',
  NOTE_MANAGE = 'note:manage',
  FEEDBACK_MANAGE = 'feedback:manage',
  INCIDENT_READ = 'incident:read',
  INCIDENT_MANAGE = 'incident:manage',
  SCREENING_READ = 'screening:read',
  VETTING_READ = 'vetting:read',
  VETTING_MANAGE = 'vetting:manage',
  VETTING_PUSH = 'vetting:push',
  CANDIDATE_READ = 'candidate:read',
  CANDIDATE_MANAGE = 'candidate:manage',
  CANDIDATE_DECIDE = 'candidate:decide',
  VETTING_BYPASS_REJECTED = 'vetting:bypass-rejected',
  // ── P5 additions (re-audit 2026-06-15) — full-surface stamping ──
  // Scoped (company/entity dimension) reads + writes absorbed by existing or new nouns.
  REFERENCE_READ = 'reference:read',
  INVOICE_READ = 'invoice:read',
  INVOICE_MANAGE = 'invoice:manage',
  COMMUNICATION_READ = 'communication:read',
  OUTREACH_READ = 'outreach:read',
  WORKFLOW_READ = 'workflow:read',
  WORKFLOW_MANAGE = 'workflow:manage',
  SCREENING_MANAGE = 'screening:manage',
  W2_READ = 'w2:read',
  OPS_CARD_READ = 'ops-card:read',
  OPS_CARD_MANAGE = 'ops-card:manage',
  AUDIT_READ = 'audit:read',
  // Elevated (global-blast / unscopeable operator capability — grantable but flagged;
  // see ELEVATED_PERMISSIONS below).
  WORKER_LIFECYCLE = 'worker:lifecycle',
  REGION_MANAGE = 'region:manage',
  W2_MANAGE = 'w2:manage',
  STAFF_READ = 'staff:read',
  STAFF_MANAGE = 'staff:manage',
  ATS_CONFIG_MANAGE = 'ats-config:manage',
  JOB_POST_MANAGE = 'job-post:manage',
  BUSINESS_REFERRAL_MANAGE = 'business-referral:manage',
  READINESS_MANAGE = 'readiness:manage',
  OUTREACH_MANAGE = 'outreach:manage',
  COMMUNICATION_BLAST = 'communication:blast',
  ESCALATION_MANAGE = 'escalation:manage',
  STRIKE_MANAGE = 'strike:manage',
  WARDEN_MANAGE = 'warden:manage',
  AGENT_MANAGE = 'agent:manage',
  STORAGE_UPLOAD = 'storage:upload',
  STORAGE_DOWNLOAD = 'storage:download',
  STORAGE_CONVERT = 'storage:convert',
  // TS-only stamp value. NOT grantable, NOT a row. Implicit for every onsite.
  BASELINE = 'baseline',
}

/**
 * Elevated permissions (P5) — grantable, but global-blast / unscopeable
 * (money movement, global config authoring, payroll/tax, cross-company bulk,
 * mass outbound). ADVISORY ONLY: no extra guard — backstop stays default-deny +
 * rank-based grant-edit authz. `OnsiteGrantsModal` surfaces a warning so the
 * granter must mean it. Mirror of the re-audit "ELEVATED" set (2026-06-15).
 */
export const ELEVATED_PERMISSIONS: ReadonlySet<OnsitePermission> = new Set([
  OnsitePermission.WORKER_LIFECYCLE,
  // shift:manage includes destructive cancel/delete (bulk shift cancellation,
  // hard-delete) — heightened security.
  OnsitePermission.SHIFT_MANAGE,
  // Company-data writes are cross-tenant config authoring — heightened security.
  OnsitePermission.COMPANY_MANAGE,
  OnsitePermission.REGION_MANAGE,
  OnsitePermission.W2_MANAGE,
  OnsitePermission.STAFF_READ,
  OnsitePermission.STAFF_MANAGE,
  OnsitePermission.ATS_CONFIG_MANAGE,
  OnsitePermission.JOB_POST_MANAGE,
  OnsitePermission.BUSINESS_REFERRAL_MANAGE,
  OnsitePermission.READINESS_MANAGE,
  OnsitePermission.OUTREACH_MANAGE,
  OnsitePermission.COMMUNICATION_BLAST,
  OnsitePermission.ESCALATION_MANAGE,
  OnsitePermission.STRIKE_MANAGE,
  OnsitePermission.WARDEN_MANAGE,
  OnsitePermission.AGENT_MANAGE,
  OnsitePermission.STORAGE_UPLOAD,
  OnsitePermission.STORAGE_DOWNLOAD,
  OnsitePermission.STORAGE_CONVERT,
])

/** True iff `p` is an elevated (global-blast) grantable permission. */
export function isElevatedGrant(p: OnsitePermission): boolean {
  return ELEVATED_PERMISSIONS.has(p)
}

/**
 * All grantable permissions — every value except BASELINE (which is implicit
 * for every onsite user and must never appear as a stored grant row).
 * Mirrors `GRANTABLE_PERMISSIONS` from the BE taxonomy.
 * Grant-writing surfaces validate against this set.
 */
export const GRANTABLE_PERMISSIONS: ReadonlySet<OnsitePermission> = new Set(
  Object.values(OnsitePermission).filter(
    (p) => p !== OnsitePermission.BASELINE,
  ) as OnsitePermission[],
)

export interface CompanyScope {
  companyId: string // legacy 32-hex IDs possible — never UUID-validate
  locations: 'ALL' | string[] // zero location rows → 'ALL'
}

/** GET /onsite-grants/me/permissions response. */
export interface MePermissionsResponse {
  // Onsite === internalUserRole EXTERNAL (the durable onsite identity anchor).
  // Internal users → false (FE treats as allow-all).
  isOnsite: boolean
  // P2 taxonomy values, RESOLVED + expanded server-side (manage-implies-read
  // materialized); `baseline` excluded (implicit). Internal → [] (allow-all).
  permissions: string[]
  // Internal → [] (unrestricted).
  companies: CompanyScope[]
}
